import moment, { Moment } from 'moment'

import { OptionGroup } from './models'

/**
 * Checks if a FormData contains a File in a `file` entry.
 *
 * @param formData FormData to be tested.
 */
export function assertFile (formData: FormData) {
  const file = formData.get('file')

  if (file === null) {
    throw new Error('Form is missing "file" entry')
  }

  if (!(file instanceof File)) {
    throw new Error('Form entry "file" is not a File')
  }
}

/**
 * Checks if a token exists.
 *
 * It returns the token.
 *
 * @param token Token to be validated.
 */
export function assertToken (token: string | null): string {
  if (token === null) {
    throw new Error('User is not authenticated')
  }

  return token
}

/**
 * Checks if two Sets are equal.
 *
 * @param a First Set.
 * @param b Second Set.
 */
export function isSetsEqual<T> (a: Set<T>, b: Set<T>) {
  return a.size === b.size && [...a].every((i) => b.has(i))
}

/**
 * Merges two arrays by replacing elements with the same id in the previous one.
 *
 * Elements in the second array are added at the end of the first one.
 *
 * @param previousData Previous array with elements that may be replaced.
 * @param newData New array to be merged with.
 */
export function mergeById<T extends { id: number }> (previousData: T[], newData: T[]): T[] {
  const ids = new Set(newData.map((i) => i.id))

  return [
    ...(previousData.filter((i) => !ids.has(i.id))),
    ...newData
  ]
}

/**
 * Tries to parse a date and formats to ISO 8601.
 *
 * @param value Date to be normalized.
 */
export function normalizeDate (value: string) {
  const date = value !== '' ? moment(value) : null

  return date && date.isValid() ? date.format('YYYY-MM-DD') : null
}

/**
 * Returns a new array with objects ordered by a `Date` in their K key.
 *
 * @param array Array with objects to be ordered.
 * @param key Key in objects with a `Date` object, which may be `null`.
 * @param options Flags to control the ordering:
 *   `isDescending` (default: `false`) controls the ordering direction;
 *   `placeMissingLast` (default: `true`) controls where to place elements
 *    without a `Date` object.
 */
export function orderByDateField<T extends Record<K, Date | null>, K extends string> (
  array: T[],
  key: K,
  {
    isDescending = false,
    placeMissingLast = true
  } = {}
) {
  const direction = isDescending ? -1 : 1
  const missingDirection = placeMissingLast ? -1 : 1

  return [...array].sort((a, b) => {
    const aDate = a[key]
    const bDate = b[key]

    if (aDate && bDate) {
      return direction * (aDate.getTime() - bDate.getTime())
    } else if (aDate) {
      return missingDirection
    } else if (bDate) {
      return missingDirection * -1
    } else {
      return 0
    }
  })
}

/**
 * Returns a new array with objects grouped by the `Date` in their K prop.
 *
 * The groups are in descending order.
 *
 * @param array Array with objects to be grouped.
 * @param prop Key in objects with a `Date` object.
 */
export function groupByDay<T extends Record<K, Date>, K extends string> (
  array: T[],
  prop: K
): [Moment, T[]][] {
  const groups: Record<number, { day: Moment; array: T[] }> = {}

  for (const element of array) {
    const day = moment(element[prop]).startOf('day')

    const key = day.unix()

    const group = groups[key]

    if (group && group.day.isSame(day)) {
      group.array.push(element)
    } else {
      groups[key] = groups[key] ?? { day, array: [] }

      groups[key].array.push(element)
    }
  }

  return Object.values(groups)
    .sort((a, b) => b.day.diff(a.day))
    .map((i) => [i.day, i.array])
}

/**
 * Returns a new array with objects ordered by the `string` value their key K.
 *
 * @param array Array with objects to be ordered.
 * @param key Key in objects with a `string` value.
 */
export function orderByLocale<T extends Record<K, string>, K extends string> (
  array: T[],
  key: K
) {
  return [...array].sort((a, b) => a[key].localeCompare(b[key]))
}

/**
 * Returns a new array with OptionGroups ordered by `sequence`, or `id` when it is `null`.
 *
 * @param optionGroups Array with OptionGroups to be ordered.
 */
export function orderByOptionGroupSequence (optionGroups: OptionGroup[]) {
  return [...optionGroups].sort((a, b) => {
    if (a.sequence !== null && b.sequence !== null && a.sequence !== b.sequence) {
      return a.sequence - b.sequence
    } else if (a.sequence !== null && b.sequence === null) {
      return -1
    } else if (a.sequence === null && b.sequence !== null) {
      return 1
    } else {
      return a.id - b.id
    }
  })
}

/**
 * Removes duplicated elements in an array by comparing a specific key.
 *
 * It keeps the first unique element.
 *
 * @param array Array to be filtered.
 * @param key Key in objects to be compared, with numeric value.
 */
export function removeDuplicates<T extends Record<K, number>, K extends string> (
  array: T[],
  key: K
) {
  const lookup = new Set<number>()

  return array.filter((obj) => !lookup.has(obj[key]) && lookup.add(obj[key]))
}

export function isFood () {
  return window.location.hostname.includes('filazero.net')
}
